import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Plus, Trash2 } from 'lucide-react'
import { ROLES, type PublicUser, type Role } from '@mlopsmena/content'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Notice, PageHeader, Spinner, ago } from '../components/ui'

/** A readable temporary password: four random words-ish chunks, ~70 bits. */
function temporaryPassword(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12))
  return Array.from(bytes, (b) => 'abcdefghjkmnpqrstuvwxyz23456789'[b % 31]).join('').replace(/(.{4})(?!$)/g, '$1-')
}

export default function Users() {
  const qc = useQueryClient()
  const { user: me } = useAuth()
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ users: PublicUser[] }>('/users').then((r) => r.users) })
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] })
  const fail = (err: unknown) => setNotice({ kind: 'error', text: (err as Error).message })

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<{ role: Role; disabled: boolean; name: string }> }) =>
      api.patch(`/users/${id}`, patch),
    onSuccess: refresh,
    onError: fail,
  })

  async function reset(u: PublicUser) {
    if (!window.confirm(`Give ${u.name} a new temporary password? They will be signed out everywhere.`)) return
    const password = temporaryPassword()
    try {
      await api.post(`/users/${u.id}/reset-password`, { password })
      setNotice({ kind: 'warn', text: `Temporary password for ${u.email}:\n${password}\nSend it to them privately; they must change it when they sign in. It is not shown again.` })
      refresh()
    } catch (err) { fail(err) }
  }

  async function remove(u: PublicUser) {
    if (!window.confirm(`Delete ${u.name}'s account for good? Disabling keeps their history instead.`)) return
    try { await api.del(`/users/${u.id}`); refresh() } catch (err) { fail(err) }
  }

  return (
    <>
      <PageHeader title="Users" />
      {notice && <div className="mb-4"><Notice kind={notice.kind}>{notice.text}</Notice></div>}
      <NewUser onCreated={(email, password) => {
        setNotice({ kind: 'warn', text: `Account created. Temporary password for ${email}:\n${password}\nSend it privately; it is not shown again.` })
        refresh()
      }} onError={fail} />

      {users.isPending && <Spinner />}
      <ul className="card mt-6 divide-y divide-line">
        {users.data?.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className={`text-sm font-medium ${u.disabled ? 'text-faint line-through' : ''}`}>{u.name} {u.id === me.id && <span className="chip">you</span>}</p>
              <p className="text-xs text-faint">
                {u.email} · last sign-in {ago(u.lastLoginAt)}
                {u.mustChangePassword && <span className="text-warn"> · temporary password</span>}
              </p>
            </div>
            <select className="input !w-auto" value={u.role} disabled={u.id === me.id}
                    onChange={(e) => update.mutate({ id: u.id, patch: { role: e.target.value as Role } })} aria-label="Role">
              {ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-xs">
              <input type="checkbox" checked={u.disabled} disabled={u.id === me.id}
                     onChange={(e) => update.mutate({ id: u.id, patch: { disabled: e.target.checked } })} />
              disabled
            </label>
            <button className="btn" disabled={u.id === me.id} onClick={() => void reset(u)} title="Reset password">
              <KeyRound className="h-3.5 w-3.5" />
            </button>
            <button className="btn text-danger" disabled={u.id === me.id} onClick={() => void remove(u)} title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-faint">Editors manage content and publish. Admins also manage users and can delete items for good.</p>
    </>
  )
}

function NewUser({ onCreated, onError }: { onCreated: (email: string, password: string) => void; onError: (e: unknown) => void }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState<Role>('editor')
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    const password = temporaryPassword()
    try {
      await api.post('/users', { email, name, role, password })
      onCreated(email, password)
      setEmail(''); setName('')
    } catch (err) { onError(err) } finally { setBusy(false) }
  }

  return (
    <form onSubmit={submit} className="card flex flex-wrap items-end gap-2 p-4">
      <div className="min-w-48 flex-1">
        <label className="label" htmlFor="new-name">Name</label>
        <input id="new-name" className="input" required value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div className="min-w-56 flex-1">
        <label className="label" htmlFor="new-email">Email</label>
        <input id="new-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <select className="input !w-auto" value={role} onChange={(e) => setRole(e.target.value as Role)} aria-label="Role">
        {ROLES.map((r) => <option key={r}>{r}</option>)}
      </select>
      <button className="btn-primary" disabled={busy}>{busy ? <Spinner /> : <Plus className="h-3.5 w-3.5" />} Add member</button>
    </form>
  )
}
